import React, { useState } from 'react';
import trackmanExportImage from '../../assets/documentation/trackman-export.png';
import gcquadExportImage from '../../assets/documentation/gcquad-export.png';
import flightscopeExportImage from '../../assets/documentation/flightscope-export.png';
import skytrakExportImage from '../../assets/documentation/skytrak-export.png';
import garminExportImage from '../../assets/documentation/garmin-export.png';
import rapsodoExportImage from '../../assets/documentation/rapsodo-export.png';

const LaunchMonitorGuides = () => {
  const [activeMonitor, setActiveMonitor] = useState('trackman');

  const handleMonitorChange = (monitor) => {
    setActiveMonitor(monitor);
  };

  return (
    <div className="launch-monitor-guides">
      <h2>Launch Monitor Export Guides</h2>
      <p className="section-intro">
        Learn how to export your session data from popular launch monitors for analysis in RangeDat. 
        Select your launch monitor below to view specific instructions.
      </p>

      <div className="monitor-selector">
        <button 
          className={activeMonitor === 'trackman' ? 'active' : ''}
          onClick={() => handleMonitorChange('trackman')}
        >
          Trackman
        </button>
        <button 
          className={activeMonitor === 'gcquad' ? 'active' : ''}
          onClick={() => handleMonitorChange('gcquad')}
        >
          GC Quad
        </button>
        <button 
          className={activeMonitor === 'flightscope' ? 'active' : ''}
          onClick={() => handleMonitorChange('flightscope')}
        >
          Flightscope
        </button>
        <button 
          className={activeMonitor === 'skytrak' ? 'active' : ''}
          onClick={() => handleMonitorChange('skytrak')}
        >
          SkyTrak
        </button>
        <button 
          className={activeMonitor === 'garmin' ? 'active' : ''}
          onClick={() => handleMonitorChange('garmin')}
        >
          Garmin R10
        </button>
        <button 
          className={activeMonitor === 'rapsodo' ? 'active' : ''}
          onClick={() => handleMonitorChange('rapsodo')}
        >
          Rapsodo
        </button>
      </div>

      {activeMonitor === 'trackman' && (
        <div className="monitor-guide">
          <h3>Trackman Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from Trackman Performance Studio</h4>
            <ol>
              <li>
                <strong>Log in to Trackman Performance Studio</strong> using your credentials at 
                <a href="https://mytrackman.com" target="_blank" rel="noopener noreferrer"> mytrackman.com</a>
              </li>
              <li>
                <strong>Navigate to the Sessions tab</strong> in the left sidebar menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export by clicking on it
              </li>
              <li>
                <strong>Click the Export button</strong> in the top right corner of the session details page
              </li>
              <li>
                <strong>Select "CSV Export"</strong> from the dropdown menu
              </li>
              <li>
                <strong>Choose "All Shots"</strong> in the export dialog
              </li>
              <li>
                <strong>Click "Export"</strong> to download the CSV file to your computer
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={trackmanExportImage} 
                alt="Trackman Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">Trackman Performance Studio export dialog</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from Trackman Mobile App</h4>
            <ol>
              <li>
                <strong>Open the Trackman app</strong> on your mobile device
              </li>
              <li>
                <strong>Go to "My Sessions"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Tap the share icon</strong> (usually in the top right corner)
              </li>
              <li>
                <strong>Select "Export as CSV"</strong> from the options
              </li>
              <li>
                <strong>Choose how to share the file</strong> (email, cloud storage, etc.)
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard Trackman data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Club Speed</li>
              <li>Smash Factor</li>
              <li>Attack Angle</li>
              <li>Club Path</li>
              <li>Face Angle</li>
              <li>Face to Path</li>
              <li>Dynamic Loft</li>
              <li>Launch Angle</li>
              <li>Launch Direction</li>
              <li>Spin Rate</li>
              <li>Spin Axis</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Height</li>
              <li>Landing Angle</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for Trackman Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Ensure club selection is accurate</strong> in your Trackman session for better analysis in RangeDat
              </li>
              <li>
                <strong>Use consistent session naming</strong> to easily identify your sessions in RangeDat
              </li>
              <li>
                <strong>Add notes to your sessions</strong> in Trackman to track swing changes or focus areas
              </li>
              <li>
                <strong>Export regularly</strong> to build a comprehensive database of your performance over time
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeMonitor === 'gcquad' && (
        <div className="monitor-guide">
          <h3>GC Quad Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from FSX Software</h4>
            <ol>
              <li>
                <strong>Open FSX Software</strong> on your computer
              </li>
              <li>
                <strong>Click on "Player Data"</strong> from the main menu
              </li>
              <li>
                <strong>Select the player profile</strong> containing your session data
              </li>
              <li>
                <strong>Navigate to the "Sessions" tab</strong>
              </li>
              <li>
                <strong>Right-click on the session</strong> you want to export
              </li>
              <li>
                <strong>Select "Export Session Data"</strong> from the context menu
              </li>
              <li>
                <strong>Choose "CSV Format"</strong> in the export dialog
              </li>
              <li>
                <strong>Select a location</strong> to save the file and click "Export"
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={gcquadExportImage} 
                alt="GC Quad Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">FSX Software export dialog</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from GC Quad Mobile App</h4>
            <ol>
              <li>
                <strong>Open the GC Quad app</strong> on your mobile device
              </li>
              <li>
                <strong>Go to "History"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Tap the "Share" button</strong> (usually in the top right corner)
              </li>
              <li>
                <strong>Select "Export as CSV"</strong> from the options
              </li>
              <li>
                <strong>Choose how to share the file</strong> (email, cloud storage, etc.)
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard GC Quad data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Total Spin</li>
              <li>Back Spin</li>
              <li>Side Spin</li>
              <li>Launch Angle</li>
              <li>Launch Direction</li>
              <li>Club Speed</li>
              <li>Smash Factor</li>
              <li>Club Path</li>
              <li>Face Angle</li>
              <li>Face to Path</li>
              <li>Attack Angle</li>
              <li>Lie Angle</li>
              <li>Loft</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Offline Distance</li>
              <li>Apex Height</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for GC Quad Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Ensure the GC Quad is properly calibrated</strong> before each session for accurate data
              </li>
              <li>
                <strong>Set up your player profile correctly</strong> with accurate club specifications
              </li>
              <li>
                <strong>Use the "Club Caddie" feature</strong> to ensure proper club selection for each shot
              </li>
              <li>
                <strong>Add environmental conditions</strong> (altitude, temperature) for more accurate distance calculations
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeMonitor === 'flightscope' && (
        <div className="monitor-guide">
          <h3>Flightscope Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from Flightscope Mevo+ App</h4>
            <ol>
              <li>
                <strong>Open the Flightscope Mevo+ app</strong> on your mobile device
              </li>
              <li>
                <strong>Tap on "History"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Tap the "Share" icon</strong> in the top right corner
              </li>
              <li>
                <strong>Select "Export as CSV"</strong> from the options
              </li>
              <li>
                <strong>Choose how to share the file</strong> (email, cloud storage, etc.)
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={flightscopeExportImage} 
                alt="Flightscope Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">Flightscope Mevo+ app export screen</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from Flightscope Web Portal</h4>
            <ol>
              <li>
                <strong>Log in to your Flightscope account</strong> at 
                <a href="https://myflightscope.com" target="_blank" rel="noopener noreferrer"> myflightscope.com</a>
              </li>
              <li>
                <strong>Navigate to "My Data"</strong> in the top menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export from the list
              </li>
              <li>
                <strong>Click the "Export" button</strong> in the session details view
              </li>
              <li>
                <strong>Select "CSV" as the export format</strong>
              </li>
              <li>
                <strong>Click "Download"</strong> to save the file to your computer
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard Flightscope data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Club Speed</li>
              <li>Smash Factor</li>
              <li>Vertical Launch Angle</li>
              <li>Horizontal Launch Angle</li>
              <li>Spin Rate</li>
              <li>Spin Axis</li>
              <li>Club Path</li>
              <li>Face Angle</li>
              <li>Face to Path</li>
              <li>Attack Angle</li>
              <li>Low Point</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Lateral Distance</li>
              <li>Apex Height</li>
              <li>Flight Time</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for Flightscope Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Ensure proper setup distance</strong> between the Flightscope unit and hitting area
              </li>
              <li>
                <strong>Set the correct club for each shot</strong> in the app for accurate club-specific data
              </li>
              <li>
                <strong>Use metallic stickers</strong> on your golf balls for improved tracking accuracy
              </li>
              <li>
                <strong>Update your Flightscope firmware regularly</strong> for the latest improvements
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeMonitor === 'skytrak' && (
        <div className="monitor-guide">
          <h3>SkyTrak Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from SkyTrak App</h4>
            <ol>
              <li>
                <strong>Open the SkyTrak app</strong> on your mobile device or computer
              </li>
              <li>
                <strong>Click on "Data & Sessions"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Click the "Export" button</strong> (usually in the top right corner)
              </li>
              <li>
                <strong>Select "CSV Format"</strong> from the export options
              </li>
              <li>
                <strong>Choose a location</strong> to save the file or share method
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={skytrakExportImage} 
                alt="SkyTrak Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">SkyTrak app export screen</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from SkyTrak Web Dashboard</h4>
            <ol>
              <li>
                <strong>Log in to your SkyTrak account</strong> at 
                <a href="https://app.skytrakgolf.com" target="_blank" rel="noopener noreferrer"> app.skytrakgolf.com</a>
              </li>
              <li>
                <strong>Navigate to "Session History"</strong> in the dashboard
              </li>
              <li>
                <strong>Find and select the session</strong> you want to export
              </li>
              <li>
                <strong>Click the "Export Data" button</strong>
              </li>
              <li>
                <strong>Select "CSV" as the format</strong>
              </li>
              <li>
                <strong>Click "Download"</strong> to save the file to your computer
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard SkyTrak data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Launch Angle</li>
              <li>Back Spin</li>
              <li>Side Spin</li>
              <li>Side Angle</li>
              <li>Total Spin</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Offline Distance</li>
              <li>Roll Distance</li>
              <li>Descent Angle</li>
              <li>Apex Height</li>
              <li>Flight Time</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for SkyTrak Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Clean the SkyTrak lens regularly</strong> for optimal ball tracking
              </li>
              <li>
                <strong>Use the correct ball position</strong> relative to the SkyTrak unit
              </li>
              <li>
                <strong>Ensure adequate lighting</strong> in your hitting area for better tracking
              </li>
              <li>
                <strong>Use high-quality golf balls</strong> with consistent markings for best results
              </li>
              <li>
                <strong>Update your SkyTrak firmware</strong> to the latest version for improved accuracy
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeMonitor === 'garmin' && (
        <div className="monitor-guide">
          <h3>Garmin R10 Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from Garmin Golf App</h4>
            <ol>
              <li>
                <strong>Open the Garmin Golf app</strong> on your mobile device
              </li>
              <li>
                <strong>Tap on "Range Sessions"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Tap the "Share" icon</strong> (usually in the top right corner)
              </li>
              <li>
                <strong>Select "Export as CSV"</strong> from the options
              </li>
              <li>
                <strong>Choose how to share the file</strong> (email, cloud storage, etc.)
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={garminExportImage} 
                alt="Garmin R10 Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">Garmin Golf app export screen</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from Garmin Connect Web Portal</h4>
            <ol>
              <li>
                <strong>Log in to your Garmin Connect account</strong> at 
                <a href="https://connect.garmin.com" target="_blank" rel="noopener noreferrer"> connect.garmin.com</a>
              </li>
              <li>
                <strong>Navigate to "Golf"</strong> in the left sidebar
              </li>
              <li>
                <strong>Select "Range Sessions"</strong> from the submenu
              </li>
              <li>
                <strong>Find and click on the session</strong> you want to export
              </li>
              <li>
                <strong>Click the "Export" button</strong> (usually represented by a download icon)
              </li>
              <li>
                <strong>Select "CSV" as the format</strong>
              </li>
              <li>
                <strong>Click "Download"</strong> to save the file to your computer
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard Garmin R10 data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Club Speed</li>
              <li>Smash Factor</li>
              <li>Club Path</li>
              <li>Face Angle</li>
              <li>Face to Path</li>
              <li>Attack Angle</li>
              <li>Launch Angle</li>
              <li>Launch Direction</li>
              <li>Spin Rate</li>
              <li>Spin Axis</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Offline Distance</li>
              <li>Apex Height</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for Garmin R10 Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Position the R10 correctly</strong> - 6-8 feet behind the ball and aligned properly
              </li>
              <li>
                <strong>Ensure the R10 is fully charged</strong> before each session
              </li>
              <li>
                <strong>Use the alignment stick</strong> provided with the R10 for proper setup
              </li>
              <li>
                <strong>Update your R10 firmware regularly</strong> through the Garmin Golf app
              </li>
              <li>
                <strong>Set the correct club</strong> for each shot in the app for accurate club-specific data
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeMonitor === 'rapsodo' && (
        <div className="monitor-guide">
          <h3>Rapsodo Export Guide</h3>
          
          <div className="guide-section">
            <h4>Exporting from Rapsodo Mobile Launch Monitor App</h4>
            <ol>
              <li>
                <strong>Open the Rapsodo MLM app</strong> on your mobile device
              </li>
              <li>
                <strong>Tap on "History"</strong> from the main menu
              </li>
              <li>
                <strong>Select the session</strong> you want to export
              </li>
              <li>
                <strong>Tap the "Share" icon</strong> in the top right corner
              </li>
              <li>
                <strong>Select "Export as CSV"</strong> from the options
              </li>
              <li>
                <strong>Choose how to share the file</strong> (email, cloud storage, etc.)
              </li>
            </ol>
            <div className="image-container">
              <img 
                src={rapsodoExportImage} 
                alt="Rapsodo Export Screenshot" 
                className="guide-image"
              />
              <p className="image-caption">Rapsodo MLM app export screen</p>
            </div>
          </div>

          <div className="guide-section">
            <h4>Exporting from Rapsodo Web Dashboard</h4>
            <ol>
              <li>
                <strong>Log in to your Rapsodo account</strong> at 
                <a href="https://dashboard.rapsodo.com" target="_blank" rel="noopener noreferrer"> dashboard.rapsodo.com</a>
              </li>
              <li>
                <strong>Navigate to "Sessions"</strong> in the left sidebar
              </li>
              <li>
                <strong>Find and select the session</strong> you want to export
              </li>
              <li>
                <strong>Click the "Export" button</strong> (usually represented by a download icon)
              </li>
              <li>
                <strong>Select "CSV" as the format</strong>
              </li>
              <li>
                <strong>Click "Download"</strong> to save the file to your computer
              </li>
            </ol>
          </div>

          <div className="guide-section">
            <h4>Supported Data Fields</h4>
            <p>
              RangeDat supports all standard Rapsodo data fields, including:
            </p>
            <ul className="data-fields-list">
              <li>Ball Speed</li>
              <li>Club Speed (Premium subscription only)</li>
              <li>Smash Factor (Premium subscription only)</li>
              <li>Launch Angle</li>
              <li>Launch Direction</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Deviation Distance</li>
              <li>Apex Height</li>
              <li>Shot Shape</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4>Tips for Rapsodo Data</h4>
            <ul className="tips-list">
              <li>
                <strong>Position the Rapsodo MLM correctly</strong> - 6-8 feet behind the ball
              </li>
              <li>
                <strong>Ensure adequate lighting</strong> for optimal camera tracking
              </li>
              <li>
                <strong>Use the alignment guide</strong> in the app for proper setup
              </li>
              <li>
                <strong>Set the correct club</strong> for each shot in the app
              </li>
              <li>
                <strong>Consider a Premium subscription</strong> for additional data metrics like club speed and smash factor
              </li>
            </ul>
          </div>
        </div>
      )}

      <div className="additional-resources">
        <h3>Additional Resources</h3>
        <div className="resources-grid">
          <div className="resource-card">
            <h4>Video Tutorials</h4>
            <p>Watch our step-by-step video guides for exporting data from various launch monitors.</p>
            <a href="/tutorials" className="resource-link">View Tutorials</a>
          </div>
          <div className="resource-card">
            <h4>Sample Files</h4>
            <p>Download sample export files to see the expected format for each launch monitor.</p>
            <a href="/samples" className="resource-link">Download Samples</a>
          </div>
          <div className="resource-card">
            <h4>Troubleshooting</h4>
            <p>Having issues with your data exports? Check our troubleshooting guide.</p>
            <a href="/troubleshooting" className="resource-link">View Troubleshooting Guide</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchMonitorGuides;
